/**
 * 统一日志系统 - 核心架构设计思想
 * 提供结构化日志、错误追踪、性能监控等功能
 */

/**
 * 日志级别枚举
 */
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

/**
 * 日志接口
 */
export interface LogEntry {
  level: LogLevel
  message: string
  module: string
  timestamp: number
  data?: any
  error?: Error
  userId?: string
  sessionId?: string
}

/**
 * 日志配置
 */
export interface LoggerConfig {
  level: LogLevel
  enableConsole: boolean
  enableStorage: boolean
  maxStorageSize: number
  enablePerformance: boolean
  enableErrorTracking: boolean
}

/**
 * 统一日志器
 */
export class Logger {
  private static instance: Logger
  private config: LoggerConfig
  private logs: LogEntry[] = []
  private performanceMarks: Map<string, number> = new Map()

  private constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      level: import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.INFO,
      enableConsole: true,
      enableStorage: import.meta.env.DEV,
      maxStorageSize: 1000,
      enablePerformance: import.meta.env.DEV,
      enableErrorTracking: true,
      ...config
    }

    // 捕获全局错误
    if (this.config.enableErrorTracking) {
      this.setupGlobalErrorHandling()
    }

    console.log('[TP_LOGGER] 日志系统初始化完成', this.config)
  }

  static getInstance(config?: Partial<LoggerConfig>): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(config)
    }
    return Logger.instance
  }

  /**
   * 设置全局错误处理
   */
  private setupGlobalErrorHandling(): void {
    // 捕获未处理的Promise错误
    window.addEventListener('unhandledrejection', event => {
      this.error('UNHANDLED_PROMISE_REJECTION', event.reason, {
        promise: event.promise,
        type: 'unhandledrejection'
      })
    })

    // 捕获未处理的JavaScript错误
    window.addEventListener('error', event => {
      this.error('UNHANDLED_ERROR', event.error || event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        type: 'error'
      })
    })

    // 捕获资源加载错误
    window.addEventListener('error', event => {
      if (event.target !== window) {
        this.error('RESOURCE_ERROR', `资源加载失败: ${(event.target as any)?.src || (event.target as any)?.href}`, {
          target: event.target,
          type: 'resource'
        })
      }
    }, true)
  }

  /**
   * 记录日志
   */
  private log(level: LogLevel, module: string, message: string, data?: any, error?: Error): void {
    if (level < this.config.level) {
      return
    }

    const entry: LogEntry = {
      level,
      message,
      module,
      timestamp: Date.now(),
      data,
      error,
      userId: this.getCurrentUserId(),
      sessionId: this.getSessionId()
    }

    // 存储日志
    if (this.config.enableStorage) {
      this.storeLog(entry)
    }

    // 控制台输出
    if (this.config.enableConsole) {
      this.outputToConsole(entry)
    }
  }

  /**
   * 存储日志
   */
  private storeLog(entry: LogEntry): void {
    this.logs.push(entry)

    // 限制存储大小
    if (this.logs.length > this.config.maxStorageSize) {
      this.logs = this.logs.slice(-this.config.maxStorageSize)
    }
  }

  /**
   * 控制台输出
   */
  private outputToConsole(entry: LogEntry): void {
    const timestamp = new Date(entry.timestamp).toISOString()
    const prefix = `[${timestamp}] [${entry.module}]`

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(prefix, entry.message, entry.data)
        break
      case LogLevel.INFO:
        console.info(prefix, entry.message, entry.data)
        break
      case LogLevel.WARN:
        console.warn(prefix, entry.message, entry.data)
        break
      case LogLevel.ERROR:
        console.error(prefix, entry.message, entry.error || entry.data)
        break
    }
  }

  /**
   * 获取当前用户ID
   */
  private getCurrentUserId(): string | undefined {
    try {
      // 这里可以从认证状态获取用户ID
      return undefined
    } catch {
      return undefined
    }
  }

  /**
   * 获取会话ID
   */
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('tp_session_id')
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      sessionStorage.setItem('tp_session_id', sessionId)
    }
    return sessionId
  }

  /**
   * Debug日志
   */
  debug(module: string, message: string, data?: any): void {
    this.log(LogLevel.DEBUG, module, message, data)
  }

  /**
   * Info日志
   */
  info(module: string, message: string, data?: any): void {
    this.log(LogLevel.INFO, module, message, data)
  }

  /**
   * Warning日志
   */
  warn(module: string, message: string, data?: any): void {
    this.log(LogLevel.WARN, module, message, data)
  }

  /**
   * Error日志
   */
  error(module: string, message: string, error?: Error | any): void {
    this.log(LogLevel.ERROR, module, message, undefined, error instanceof Error ? error : new Error(String(error)))
  }

  /**
   * 性能标记开始
   */
  markStart(name: string): void {
    if (this.config.enablePerformance) {
      this.performanceMarks.set(name, performance.now())
      this.debug('PERFORMANCE', `性能标记开始: ${name}`)
    }
  }

  /**
   * 性能标记结束
   */
  markEnd(name: string): number | undefined {
    if (this.config.enablePerformance) {
      const startTime = this.performanceMarks.get(name)
      if (startTime !== undefined) {
        const duration = performance.now() - startTime
        this.performanceMarks.delete(name)
        this.info('PERFORMANCE', `性能标记结束: ${name}`, { duration: `${duration.toFixed(2)}ms` })
        return duration
      }
    }
    return undefined
  }

  /**
   * 获取日志
   */
  getLogs(level?: LogLevel): LogEntry[] {
    if (level !== undefined) {
      return this.logs.filter(log => log.level >= level)
    }
    return [...this.logs]
  }

  /**
   * 清空日志
   */
  clearLogs(): void {
    this.logs = []
    this.info('LOGGER', '日志已清空')
  }

  /**
   * 导出日志
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2)
  }

  /**
   * 获取日志统计
   */
  getLogStats() {
    const stats = {
      total: this.logs.length,
      debug: 0,
      info: 0,
      warn: 0,
      error: 0
    }

    this.logs.forEach(log => {
      switch (log.level) {
        case LogLevel.DEBUG:
          stats.debug++
          break
        case LogLevel.INFO:
          stats.info++
          break
        case LogLevel.WARN:
          stats.warn++
          break
        case LogLevel.ERROR:
          stats.error++
          break
      }
    })

    return stats
  }
}

/**
 * 错误边界装饰器
 */
export function ErrorBoundary(module: string) {
  return function(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value

    descriptor.value = async function(...args: any[]) {
      try {
        const result = await originalMethod.apply(this, args)
        return result
      } catch (error) {
        logger.error(module, `方法执行失败: ${propertyKey}`, error)
        throw error
      }
    }

    return descriptor
  }
}

/**
 * 性能监控装饰器
 */
export function Performance(module: string) {
  return function(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value

    descriptor.value = async function(...args: any[]) {
      const markName = `${module}.${propertyKey}`
      logger.markStart(markName)

      try {
        const result = await originalMethod.apply(this, args)
        return result
      } finally {
        logger.markEnd(markName)
      }
    }

    return descriptor
  }
}

/**
 * 创建模块日志器
 */
export function createModuleLogger(module: string) {
  return {
    debug: (message: string, data?: any) => logger.debug(module, message, data),
    info: (message: string, data?: any) => logger.info(module, message, data),
    warn: (message: string, data?: any) => logger.warn(module, message, data),
    error: (message: string, error?: Error | any) => logger.error(module, message, error),
    markStart: (name: string) => logger.markStart(`${module}.${name}`),
    markEnd: (name: string) => logger.markEnd(`${module}.${name}`)
  }
}

// 导出单例实例
export const logger = Logger.getInstance()

// 开发环境下将日志器挂载到全局对象
if (import.meta.env.DEV) {
  (window as any).__logger = logger
  console.log('[TP_LOGGER] 开发环境下可使用 window.__logger 进行调试')
}
